import { ReactNode } from 'react';

import classNames from 'classnames';

import { blockInfo } from '../../../blockInfo';
import { JourneyDraft } from '../../../draft';
import { Selection } from '../../../selection';

interface JourneyTreeProps {
  draft: JourneyDraft;
  selection: Selection;
  issuesCount: number;
  locked: boolean;
  onSelect: (selection: Selection) => void;
  onAddMile: () => void;
  onAddStep: (mileKey: string) => void;
}

interface RowProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}

const Row = ({ active, onClick, children, className }: RowProps) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm hover:bg-neutral-10',
      { '!bg-primary-10 text-primary-60 font-semibold': active, 'text-neutral-80': !active },
      className,
    )}
  >
    {children}
  </button>
);

const AddRow = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full px-2 py-1.5 text-left text-sm font-semibold text-primary-50 hover:text-primary-40"
  >
    + {label}
  </button>
);

const Caret = ({ open }: { open: boolean }) => (
  <span className={classNames('text-neutral-40 transition-transform', { 'rotate-90': open })}>
    ›
  </span>
);

const JourneyTree = ({
  draft,
  selection,
  issuesCount,
  locked,
  onSelect,
  onAddMile,
  onAddStep,
}: JourneyTreeProps) => {
  const selectedMileKey = 'mileKey' in selection ? selection.mileKey : undefined;
  const selectedStepKey = 'stepKey' in selection ? selection.stepKey : undefined;
  const selectedBlockKey = 'blockKey' in selection ? selection.blockKey : undefined;

  return (
    <nav className="grid gap-1 rounded-2xl bg-white-100 p-2 self-start">
      <Row active={selection.kind === 'info'} onClick={() => onSelect({ kind: 'info' })}>
        <img src="/icons/info.svg" alt="" className="w-4 h-4" />
        <span className="flex-1">Journey info</span>
        {issuesCount > 0 && (
          <span className="px-1.5 rounded-md bg-danger-10 text-danger-60 text-xs font-semibold">
            {issuesCount}
          </span>
        )}
      </Row>

      <div className="border-t border-neutral-10 my-1" />

      {!draft.miles.length && (
        <p className="px-2 py-1 text-xs text-neutral-50">
          No miles yet. A journey is walked mile by mile — add the first one.
        </p>
      )}

      {draft.miles.map((mile, mileIndex) => {
        const mileOpen = mile.key === selectedMileKey;

        return (
          <div key={mile.key}>
            <Row
              active={selection.kind === 'mile' && mileOpen}
              onClick={() => onSelect({ kind: 'mile', mileKey: mile.key })}
            >
              <Caret open={mileOpen} />
              <span
                className="flex items-center justify-center w-6 h-6 rounded-md text-xs font-bold shrink-0"
                style={{ backgroundColor: `${mile.color}1F`, color: mile.color }}
              >
                {mileIndex + 1}
              </span>
              <span className="min-w-0">
                <span className="block text-[11px] font-normal text-neutral-50">
                  Mile {mileIndex + 1}
                </span>
                <span className="block truncate">{mile.title || 'Untitled mile'}</span>
              </span>
            </Row>

            {mileOpen && (
              <div className="ml-4 pl-2 border-l border-neutral-10">
                {mile.steps.map((step, stepIndex) => {
                  const stepOpen = step.key === selectedStepKey;

                  return (
                    <div key={step.key}>
                      <Row
                        active={selection.kind === 'step' && stepOpen}
                        onClick={() =>
                          onSelect({ kind: 'step', mileKey: mile.key, stepKey: step.key })
                        }
                      >
                        <Caret open={stepOpen} />
                        <span className="text-xs text-neutral-50 shrink-0">
                          {mileIndex + 1}.{stepIndex + 1}
                        </span>
                        <span className="truncate">{step.title || 'Untitled step'}</span>
                      </Row>

                      {stepOpen && (
                        <div className="ml-5">
                          {step.blocks.map((block) => {
                            const { icon, title } = blockInfo(block);

                            return (
                              <Row
                                key={block.key}
                                active={block.key === selectedBlockKey}
                                onClick={() =>
                                  onSelect({
                                    kind: 'block',
                                    mileKey: mile.key,
                                    stepKey: step.key,
                                    blockKey: block.key,
                                  })
                                }
                              >
                                {icon && <img src={icon} alt="" className="w-4 h-4" />}
                                <span className="truncate">{title}</span>
                              </Row>
                            );
                          })}
                          {!locked && (
                            <AddRow
                              label="Add block"
                              onClick={() =>
                                onSelect({ kind: 'step', mileKey: mile.key, stepKey: step.key })
                              }
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!locked && <AddRow label="Add step" onClick={() => onAddStep(mile.key)} />}
                <Row
                  active={selection.kind === 'mileComplete' && mileOpen}
                  onClick={() => onSelect({ kind: 'mileComplete', mileKey: mile.key })}
                >
                  <span>🏁</span>
                  <span>Mile complete</span>
                </Row>
              </div>
            )}
          </div>
        );
      })}

      {!locked && (
        <button
          type="button"
          onClick={onAddMile}
          className="mt-1 py-2 rounded-lg border border-dashed border-neutral-30 text-sm font-semibold text-primary-50 hover:bg-primary-10"
        >
          + Add mile
        </button>
      )}

      <div className="border-t border-neutral-10 my-1" />

      <Row
        active={selection.kind === 'journeyComplete'}
        onClick={() => onSelect({ kind: 'journeyComplete' })}
      >
        <span>🏆</span>
        <span>Journey complete</span>
      </Row>
    </nav>
  );
};

export default JourneyTree;
